import useRest from "../useRest";
import {
  ICreateUsuarioParams,
  IDeleteUsuarioParams,
  IGetAllUsuarioPaginatedParams,
  IGetAllUsuarioResponse,
  IGetUsuarioByIdParams,
  IUsuario,
  IUsuarioForm,
  IUsuarioResponse,
  UsuarioForm,
  IPermissionsResponse,
  IPermission,
  IWhoAmIResponse,
} from "../../model/Usuario";
import { IDefaultOption } from "../../model/utils";
import {
  notifySuccessfullyRemoved,
  notifySuccessfullySaved,
} from "../../services/alertApi";

const useUsuario = () => {
  const { post, put, get, remove } = useRest();

  const getAllUsuario = async () => {
    const url = `/usuario/list`;
    const response = await get<IGetAllUsuarioResponse>({ url });

    return response?.data;
  };

  const getUsuarioById = async ({ id }: IGetUsuarioByIdParams) => {
    const url = `/usuario/list/${id}`;
    const response = await get<IUsuario>({ url });

    return response?.data;
  };

  const getAllUsuarioPaginated = async ({
    page,
    pageSize,
    filters,
  }: IGetAllUsuarioPaginatedParams) => {
    const url = "/usuario/list";

    const params: any = {};
    if (filters?.usuario_id) {
      params.id = filters.usuario_id;
    }

    const response = await get<IGetAllUsuarioResponse>({ url, params });

    let allItems: IUsuario[] = [];
    
    if (Array.isArray(response?.data)) {
      allItems = response.data;
    } else if (response?.data?.Items) {
      allItems = response.data.Items;
    }

    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = allItems.slice(startIndex, endIndex);
    
    const totalItems = allItems.length;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      Items: paginatedItems,
      Pagination: {
        currentPage: page,
        totalPages,
        pageSize,
        totalItems,
      },
    };
  };

  const createUsuario = async ({ data }: ICreateUsuarioParams) => {
    const url = `/usuario/criar`;
    const response = await post({ url, body: data });
    notifySuccessfullySaved();

    return response?.data;
  };

  const editUsuario = async ({ data }: ICreateUsuarioParams) => {
    const url = `/usuario/editar/${data.id}`;
    const response = await post({ url, body: data });
    notifySuccessfullySaved();

    return response?.data;
  };

  const searchUsuarioByNome = async (searchVal: string) => {
    const url = "/usuario/list";
    const response = await get<IGetAllUsuarioResponse>({
      url,
      ignoreLoading: true,
    });

    let allItems: IUsuario[] = [];
    
    if (Array.isArray(response?.data)) {
      allItems = response.data;
    } else if (response?.data?.Items) {
      allItems = response.data.Items;
    }

    const filteredItems = allItems.filter((item) =>
      item.nome?.toLowerCase().includes(searchVal.toLowerCase())
    );

    return {
      Items: filteredItems,
      Count: filteredItems.length,
    };
  };

  const deleteUsuario = async ({ id, onCloseAlert }: IDeleteUsuarioParams) => {
    const url = `/usuario/deletar/${id}`;
    const response = await remove({ url });
    notifySuccessfullyRemoved(onCloseAlert);
    return response?.data;
  };

  const getPermissions = async (): Promise<IPermissionsResponse> => {
    const url = `/usuario/roles`;
    const response = await get<IPermissionsResponse>({ url });
    return response?.data;
  };

  const getRolePermissions = async (role: string): Promise<IPermission[]> => {
    const url = `/usuario/role/permissions?role=${encodeURIComponent(role)}`;
    const response = await get<IPermission[]>({ url });
    return response?.data || [];
  };

  const whoami = async (): Promise<IWhoAmIResponse> => {
    const url = `/whoami`;
    const response = await get<IWhoAmIResponse>({ url });
    return response?.data;
  };

  const alterarSenha = async (userId: string, newPassword: string) => {
    const url = `/usuario/alterar-senha/${userId}`;
    const body = {
      new_password: newPassword,
    };
    const response = await post({ url, body });
    return response?.data;
  };

  const buildUsuarioResponsavelOption = (
    usuarioNome: string,
    usuarioId: string
  ): IDefaultOption => ({
    label: usuarioNome,
    value: usuarioId,
  });

  const parseFormUsuario = (form: IUsuario): IUsuarioForm => ({
    id: form.id,
    nome: form.nome,
    email: form.email,
    telefone: form.telefone,
    role: form.role || null,
    permissoes: form.permissoes || [],
    certificado: form.certificado || null,
  });

  const defaultValuesUsuario: IUsuarioForm = {
    id: null,
    nome: "",
    email: "",
    password: "",
    telefone: "",
    role: null,
    permissoes: [],
    certificado: "",
  };

  return {
    getAllUsuario,
    searchUsuarioByNome,
    buildUsuarioResponsavelOption,
    parseFormUsuario,
    getAllUsuarioPaginated,
    defaultValuesUsuario,
    deleteUsuario,
    createUsuario,
    editUsuario,
    getUsuarioById,
    getPermissions,
    getRolePermissions,
    whoami,
    alterarSenha,
  };
};

export default useUsuario;
